import { cva } from 'class-variance-authority'
import { clsx } from 'clsx'
import { PropertyValueMap, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html, unsafeStatic } from 'lit/static-html.js'

import { anchorClass } from '@/common/const'

import { Common, Size } from '../../common/types'
import { DdsElement } from '../../internal/dds-hu-element'
import styles from './_styles/typography.scss?inline'

export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'body'
  | 'caption'
  | 'description'

export type TypographySize =
  | Extract<Size, 'lg' | 'md' | 'sm'>
  | null
  | undefined

type HeadingProps = {
  variant: Extract<TypographyVariant, 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'>
  size?: TypographySize
}

type BodyProps = {
  variant: Extract<TypographyVariant, 'body'>
  size: TypographySize
}

type CaptionProps = {
  variant: Extract<TypographyVariant, 'caption'>
  size: never
}

type DescriptionProps = {
  variant: Extract<TypographyVariant, 'description'>
  size: Extract<TypographySize, 'lg' | 'sm'>
}

export type TypographyProps =
  | HeadingProps
  | BodyProps
  | CaptionProps
  | DescriptionProps

const typographyStyles = cva('typography', {
  variants: {
    variant: {
      h1: 'heading typography-h1',
      h2: 'heading typography-h2',
      h3: 'heading typography-h3',
      h4: 'heading typography-h4',
      h5: 'heading typography-h5',
      h6: 'heading typography-h6',
      body: 'typography-body',
      caption: 'typography-caption',
      description: 'typography-description',
    },
    size: {
      lg: 'typography-size-lg',
      md: 'typography-size-md',
      sm: 'typography-size-sm',
      xs: 'typography-size-xs',
    },
  },
  compoundVariants: [
    {
      variant: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
      class: '',
    },
    {
      variant: 'description',
      size: 'lg',
      class: 'typography-description-lg',
    },
    {
      variant: 'description',
      size: 'sm',
      class: 'typography-description-sm',
    },
  ],
})

/**
 * @element dap-ds-typography
 * @summary Typography is used to display text in different styles.
 * @title - Typography
 *
 * @slot default - The content of the typography.
 *
 * @csspart base - The main typography container.
 *
 * @cssproperty --dds-text-color - The color of the typography.
 * @cssproperty --dds-text-heading-color - The color of the heading typography.
 * @cssproperty --dds-text-description-subtle - The color of the subtle typography.
 * @cssproperty --dds-text-font-family - The font family of the typography.
 * @cssproperty --dds-text-font-weight-medium - The font weight of the medium typography.
 * @cssproperty --dds-text-font-weight-bold - The font weight of the bold typography.
 * @cssproperty --dds-text-font-size-h1 - The font size of the h1 typography.
 * @cssproperty --dds-text-font-size-h2 - The font size of the h2 typography.
 * @cssproperty --dds-text-font-size-h3 - The font size of the h3 typography.
 * @cssproperty --dds-text-font-size-h4 - The font size of the h4 typography.
 * @cssproperty --dds-text-font-size-h5 - The font size of the h5 typography.
 * @cssproperty --dds-text-font-size-h6 - The font size of the h6 typography.
 * @cssproperty --dds-text-font-size-body - The font size of the body typography.
 * @cssproperty --dds-text-font-size-caption - The font size of the caption typography.
 * @cssproperty --dds-text-font-size-description - The font size of the description typography.
 * @cssproperty --dds-text-font-size-description-lg - The font size of the description lg typography.
 * @cssproperty --dds-text-font-size-description-sm - The font size of the description sm typography.
 * @cssproperty --dds-text-font-size-lg - The font size of the lg typography.
 * @cssproperty --dds-text-font-size-md - The font size of the md typography.
 * @cssproperty --dds-text-font-size-sm - The font size of the sm typography.
 * @cssproperty --dds-text-font-size-xs - The font size of the xs typography.
 */
@customElement('dap-ds-typography')
export default class DapDSTypography
  extends DdsElement
  implements Common<TypographyProps>
{
  /**
   * The variant of the typography.
   * @type {'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body' | 'caption' | 'description'}
   */
  @property({ reflect: true }) variant!: TypographyVariant
  /**
   * The size of the typography.
   * @type {'sm' | 'md' | 'lg'}
   */
  @property({ reflect: true }) size?: TypographySize
  /**
   * The html element of the body typography.
   */
  @property({ reflect: true }) bodyAs?: string = 'p'
  /** The custom class of the typography. */
  @property() customClass?: string
  /** Whether the typography is an anchor for the TOC component. */
  @property({ type: Boolean, reflect: true }) anchor = false

  static readonly styles = unsafeCSS(styles)

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)
    if (this.elementId) {
      this.setAttribute('id', this.elementId)
    }
  }

  private get elementId() {
    return this.id ? this.id : undefined
  }

  private _renderHeading(
    elementId: string | undefined,
    customClass: string,
    variant: TypographyVariant,
    size: TypographySize,
  ) {
    return html`
      <${unsafeStatic(variant)}
        part="base"
        id=${ifDefined(elementId)}
        class=${clsx(
          typographyStyles({ variant, size }),
          { [anchorClass]: this.anchor },
          customClass,
        )}
      >
       <slot></slot>
      </${unsafeStatic(variant)}>
    `
  }

  private _renderBody(
    elementId: string | undefined,
    customClass: string,
    size: TypographySize,
    bodyAs: string,
  ) {
    return html`
      <${unsafeStatic(bodyAs)}
        id=${ifDefined(elementId)}
        part="base"
        class=${clsx(typographyStyles({ variant: 'body', size }), customClass)}
      >
        <slot></slot>
      </${unsafeStatic(bodyAs)}>
    `
  }

  private _renderCaption(elementId: string | undefined, customClass: string) {
    return html`
      <span
        id=${ifDefined(elementId)}
        part="base"
        class=${clsx(typographyStyles({ variant: 'caption' }), customClass)}>
        <slot></slot>
      </span>
    `
  }

  private _renderDescription(
    elementId: string | undefined,
    customClass: string,
    size: TypographySize,
  ) {
    return html`
      <span
        id=${ifDefined(elementId)}
        part="base"
        class=${clsx(
          typographyStyles({ variant: 'description', size }),
          customClass,
        )}>
        <slot></slot>
      </span>
    `
  }

  private _getElement(
    elementId: string | undefined,
    variant: TypographyVariant,
    size: TypographySize,
    customClass: string = '',
    bodyAs: string = '',
  ) {
    switch (variant) {
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
        return this._renderHeading(elementId, customClass, variant, size)
      case 'body':
        return this._renderBody(elementId, customClass, size, bodyAs)
      case 'caption':
        return this._renderCaption(elementId, customClass)
      case 'description':
        return this._renderDescription(elementId, customClass, size)
      default:
        return null
    }
  }

  render() {
    return this._getElement(
      this.elementId,
      this.variant,
      this.size,
      this.customClass,
      this.bodyAs,
    )
  }
}
